import { computed, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'

import { studentJourney } from '@/services/journey.service'

import type { CompetitionGamification } from '@/types/types'

export type JourneysStatus = 'initial' | 'loading' | 'success' | 'error'

export interface JourneyListItem {
  id: number | string
  title: string
  subtitle: string
  image: string
  progress: number
  status: string
  competitions?: CompetitionGamification[] | null
}

// 2 Minutos (Debater sobre isso)
const CACHE_TTL_MS = 2 * 60 * 1000

export const useJourneys = createGlobalState(() => {
  const status = ref<JourneysStatus>('initial')
  const rawItems = ref<any[]>([])
  const lastLoadedAt = ref<number | null>(null)
  const statusOrder: Record<string, number> = {
    EmAndamento: 1,
    Pendente: 2,
    Concluida: 3
  }

  const isStale = () => {
    if (!lastLoadedAt.value) return true
    return Date.now() - lastLoadedAt.value > CACHE_TTL_MS
  }

  const load = async (force = false) => {
    if (status.value === 'loading') return
    if (!force && status.value === 'success' && !isStale()) return

    status.value = 'loading'

    try {
      const response = await studentJourney.getAll()

      const data = (response.data || [])?.map((item: any) => {
        if (item.porcentagemDeConclusao === 100 && item.status !== 'Concluida') {
          item.status = 'Concluida'
        }
        return item
      })

      rawItems.value = data?.sort((a: any, b: any) => {
        const orderA = statusOrder[a?.status] || 99
        const orderB = statusOrder[b?.status] || 99
        return orderA - orderB
      })

      status.value = 'success'
      lastLoadedAt.value = Date.now()
    } catch (error) {
      console.error('Erro ao carregar jornadas do usuário:', error)
      status.value = 'error'
    }
  }

  const clear = () => {
    rawItems.value = []
    status.value = 'initial'
    lastLoadedAt.value = null
  }

  const list = computed(() => {
    if (status.value !== 'loading' && (status.value === 'initial' || status.value === 'error' || isStale())) {
      load()
    }

    return rawItems.value.map(e => ({
      id: e.id,
      title: e.titulo,
      subtitle: e.observacao,
      image: e.imagemDeCapa,
      progress: getProgress(e),
      status: e.status,
      competitions: e.competitions
    })) as JourneyListItem[]
  })

  const raw = computed(() => {
    if (status.value !== 'loading' && (status.value === 'initial' || status.value === 'error' || isStale())) {
      load()
    }

    return rawItems.value
  })

  const getProgress = (item: any) => {
    if (!item) return 0
    if (item.porcentagemDeConclusao == 100) return item.porcentagemDeConclusao

    const conteudos = item?.jornadasConteudos || []
    if (conteudos.length === 0) return 0

    let completedCount = 0
    for (const conteudo of conteudos) {
      if (conteudo.concluido) {
        completedCount++
      } else {
        break
      }
    }

    return (completedCount / conteudos.length) * 100
  }

  const syncProgress = (id: number | string, progress: number) => {
    const item = list.value.find(i => i.id === id)

    if (item) {
      item.progress = progress
    }
  }

  return {
    status,
    load,
    clear,
    list,
    raw,
    syncProgress
  }
})